
import React, { useState, useRef, useEffect } from 'react';
import { Button, Card, Container } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import AssignmentsCard from './AdminCards/AssignmentsCard';
import AttendanceCard from './AdminCards/AttendanceCard';
import PollCard from './AdminCards/PollsCard';
import TimerCard from './AdminCards/TimerCard';
import RemoteDisplayRegistration from './RemoteDisplayRegistration';
import EventsCard from './AdminCards/EventsCard';
import RolesCard from './AdminCards/RolesCard';
import EvaluationTopicsCard from './AdminCards/EvaluationTopicsCard';
import StandardModal from 'components/general/defaultModals/StandardModal';
import ContestHubSetupModal from './ContestHubSetupModal';
import { getContestPluginConfig } from '../../services/contestPlugin';
import { getUserInitials, resolveAvatarSrc } from 'utils/avatar';
import RightSidebar from 'components/navigation/RightSidebar';

const TOOLS = [
  { key: 'assignments', label: 'Role Assignments', icon: 'bi bi-person-gear' },
  { key: 'attendance',  label: 'Attendance',      icon: 'bi bi-people-fill' },
  { key: 'polls',       label: 'Polls',            icon: 'bi bi-bar-chart' },
  { key: 'timer',       label: 'Time Report',      icon: 'bi bi-clock-history' },
  { key: 'registerDisplay', label: 'Register This Screen', icon: 'bi bi-display' },
  { key: 'remoteDisplays', label: 'Remote Displays', icon: 'bi bi-display' },
  { key: 'events',      label: 'Events',           icon: 'bi bi-calendar-event' },
  { key: 'roles',       label: 'Roles',            icon: 'bi bi-people' },
  { key: 'evaluationTopics', label: 'Evaluation Topics', icon: 'bi bi-stars' },
  { key: 'contestHub', label: 'ContestHub', icon: 'bi bi-trophy' },
];

export default function ConfigToolbar({ getMeeting, getRoleAssignment, assignments, agenda, meeting, isLocked = false, remoteDisplayRegistration }) {
  const { user: authUser } = useSelector((state) => state.auth);
  const [menuOpen, setMenuOpen]   = useState(false);
  const [activeTool, setActiveTool] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [reloading, setReloading] = useState(false);
  const [toolCounts, setToolCounts] = useState({ polls: 0, events: 0, roles: 0 });
  const [contestTypes, setContestTypes] = useState([]);
  const [contestJudgeEntries, setContestJudgeEntries] = useState([]);
  const [selectedRandomizerContest, setSelectedRandomizerContest] = useState('');
  const [randomizerOrder, setRandomizerOrder] = useState({ contestType: '', agendaItemIds: [] });
  const [randomizerRound, setRandomizerRound] = useState(0);
  const [isRandomizing, setIsRandomizing] = useState(false);
  const [randomizedContestTypes, setRandomizedContestTypes] = useState([]);
  const [isSavingRandomizerOrder, setIsSavingRandomizerOrder] = useState(false);

  const toggleRef = useRef(null);
  const panelRef  = useRef(null);
  const randomizerTimeoutsRef = useRef([]);

  function clearRandomizerTimeouts() {
    randomizerTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
    randomizerTimeoutsRef.current = [];
  }

  function shuffleAgendaItemIds(agendaItemIds) {
    const shuffledIds = [...agendaItemIds];
    for (let index = shuffledIds.length - 1; index > 0; index -= 1) {
      const randomIndex = Math.floor(Math.random() * (index + 1));
      [shuffledIds[index], shuffledIds[randomIndex]] = [shuffledIds[randomIndex], shuffledIds[index]];
    }
    if (shuffledIds.every((id, index) => id === agendaItemIds[index])) {
      shuffledIds.push(shuffledIds.shift());
    }
    return shuffledIds;
  }

  function selectRandomizerContest(contestType) {
    clearRandomizerTimeouts();
    setIsRandomizing(false);
    setRandomizerOrder({ contestType: '', agendaItemIds: [] });
    setSelectedRandomizerContest(contestType);
  }

  function randomizePresenters(agendaItemIds) {
    if (isRandomizing || agendaItemIds.length < 2) return;
    if (randomizedContestTypes.includes(selectedRandomizerContest)
      && !window.confirm('Randomize these presenters again? The current randomized order will be replaced.')) return;

    clearRandomizerTimeouts();
    setRandomizedContestTypes((current) => current.includes(selectedRandomizerContest)
      ? current
      : [...current, selectedRandomizerContest]
    );
    setIsRandomizing(true);
    let nextOrder = [...agendaItemIds];

    for (let round = 1; round <= 10; round += 1) {
      nextOrder = shuffleAgendaItemIds(nextOrder);
      const shuffledOrder = nextOrder;
      const timeoutId = window.setTimeout(() => {
        setRandomizerOrder({ contestType: selectedRandomizerContest, agendaItemIds: shuffledOrder });
        setRandomizerRound(round);
        if (round === 10) {
          setIsRandomizing(false);
          randomizerTimeoutsRef.current = [];
        }
      }, round * 300);
      randomizerTimeoutsRef.current.push(timeoutId);
    }
  }

  function getAgendaItemId(item) {
    return String(item?._id || item?.id || item?.agendaItemId || item?.itemId || '').trim();
  }

  useEffect(() => () => clearRandomizerTimeouts(), []);

  useEffect(() => {
    if (!menuOpen) return;
    function onPointerDown(e) {
      if (panelRef.current  && panelRef.current.contains(e.target))  return;
      if (toggleRef.current && toggleRef.current.contains(e.target)) return;
      setMenuOpen(false);
    }
    function onKeyDown(e) {
      if (e.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const handleToolClick = (key) => {
    setActiveTool(key);
    setShowModal(true);
    setMenuOpen(false);
  };

  const updateToolCount = (key, count) => {
    const normalizedCount = Number.isFinite(Number(count)) ? Number(count) : 0;
    setToolCounts((current) => {
      if (current[key] === normalizedCount) {
        return current;
      }
      return {
        ...current,
        [key]: normalizedCount,
      };
    });
  };

  const handleCloseModal = () => {
    clearRandomizerTimeouts();
    setIsRandomizing(false);
    setShowModal(false);
    setActiveTool(null);
  };

  const handleToggleMeetingLock = async () => {
    if (isUpdatingLock || !meetingId) return;
    const wantsUnlock = !isTemporarilyUnlocked;
    const confirmMessage = wantsUnlock
      ? 'Unlock this meeting for everyone for the next 2 hours?'
      : 'Lock this meeting again right now?';
    if (!window.confirm(confirmMessage)) return;

    setIsUpdatingLock(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_URL}/meeting/${meetingId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authUser?.token || ''}`,
        },
        body: JSON.stringify({ unlockedUntil: wantsUnlock }),
      });
      if (!response.ok) throw new Error('Failed to update the meeting lock state.');
      await Promise.resolve(getMeeting?.());
      toast.success(wantsUnlock ? 'Meeting unlocked for 2 hours.' : 'Meeting locked.', { position: 'bottom-left' });
    } catch (error) {
      toast.error(error.message || 'Failed to update the meeting lock state.', { position: 'bottom-left' });
    } finally {
      setIsUpdatingLock(false);
      setMenuOpen(false);
    }
  };

  const handleReloadModalContent = async () => {
    if (reloading) return;

    setReloading(true);
    try {
      await Promise.all([
        Promise.resolve(getMeeting?.()),
        Promise.resolve(getRoleAssignment?.()),
      ]);
      setReloadTick((current) => current + 1);
    } finally {
      setReloading(false);
    }
  };

  const isContestMeeting = meeting?.meetingDetails?.isContest === true;
  const meetingId = meeting?.meetingDetails?.id || meeting?.meetingDetails?._id;
  const isWebmaster = Boolean(authUser?.user?.isWebmaster);
  const unlockedUntil = meeting?.meetingDetails?.unlockedUntil ? new Date(meeting.meetingDetails.unlockedUntil) : null;
  const isTemporarilyUnlocked = Boolean(unlockedUntil && unlockedUntil.getTime() > Date.now());
  const [isUpdatingLock, setIsUpdatingLock] = useState(false);
  const meetingSpecialAssignments = meeting?.meetingDetails?.specialAssignments || meeting?.specialAssignments || {};
  const authIdentityIds = [
    authUser?.user?.localUserId,
    authUser?.user?._id,
    authUser?.user?.id,
    authUser?.user?.tmAuthUserId,
  ].map((value) => String(value || '').trim()).filter(Boolean);
  const isTimerManager = Boolean(
    authIdentityIds.length
    && authIdentityIds.includes(String(meetingSpecialAssignments.timerManager || '').trim())
  );
  const assignedContestTypeIds = Array.isArray(meeting?.meetingDetails?.contestTypes)
    ? meeting.meetingDetails.contestTypes.map(String)
    : [];
  const assignedContestTypeKey = assignedContestTypeIds.join('|');

  useEffect(() => {
    if (!isContestMeeting || !authUser?.token) {
      setContestTypes([]);
      return;
    }

    getContestPluginConfig(authUser.token)
      .then((config) => setContestTypes(Array.isArray(config?.contestTypes) ? config.contestTypes : []))
      .catch(() => setContestTypes([]));
  }, [authUser?.token, isContestMeeting]);

  useEffect(() => {
    if (activeTool !== 'contestRandomizer' || !authUser?.token || !meetingId) return;

    fetch(`${process.env.REACT_APP_URL}/contesthub/meetings/${meetingId}/setup`, {
      headers: { Authorization: `Bearer ${authUser.token}` }
    })
      .then((response) => response.ok ? response.json() : null)
      .then((setup) => {
        const assignedContestTypeIdSet = new Set(assignedContestTypeKey.split('|').filter(Boolean));
        const entries = (Array.isArray(setup?.contestJudges) ? setup.contestJudges : [])
          .filter((entry) => assignedContestTypeIdSet.has(String(entry?.contestType || '')));
        setContestJudgeEntries(entries);
        setSelectedRandomizerContest((current) => entries.some((entry) => String(entry?.contestType || '') === current)
          ? current
          : String(entries[0]?.contestType || '')
        );
      })
      .catch(() => {
        setContestJudgeEntries([]);
        setSelectedRandomizerContest('');
      });
  }, [activeTool, assignedContestTypeKey, authUser?.token, meetingId]);

  const meetingContests = contestTypes.filter((contest) =>
    assignedContestTypeIds.includes(String(contest?._id || contest?.id || ''))
  );
  const contestTools = meetingContests.map((contest) => ({
    key: `contest:${contest._id || contest.id}`,
    label: contest.name,
    icon: 'bi bi-trophy'
  }));

  const visibleTools = [...TOOLS.filter((tool) => {
    if (tool.key === 'contestHub') {
      return isContestMeeting;
    }

    if (tool.key === 'polls') {
      return !isContestMeeting;
    }

    if (tool.key === 'remoteDisplays') {
      return isTimerManager;
    }

    if (tool.key !== 'evaluationTopics') {
      return true;
    }

    return authUser?.user?.acl === 'Officer';
  }), ...contestTools].map((tool) => (
    tool.key === 'registerDisplay' && remoteDisplayRegistration?.registered
      ? { ...tool, label: 'Unregister This Screen', icon: 'bi bi-display-fill' }
      : tool
  ));

  let modalTitle = '';
  let modalFooterMeta = null;
  let modalHeaderActions = null;
  let ModalBodyComponent = null;
  const activeContest = activeTool?.startsWith('contest:')
    ? meetingContests.find((contest) => String(contest?._id || contest?.id || '') === activeTool.slice(8))
    : null;
  const agendaEntries = Array.isArray(agenda) && agenda.length > 0
    ? agenda
    : Array.isArray(meeting?.items) && meeting.items.length > 0
      ? meeting.items
      : Array.isArray(meeting?.meetingDetails?.items) && meeting.meetingDetails.items.length > 0
        ? meeting.meetingDetails.items
        : Array.isArray(meeting?.meetingDetails?.agenda) && meeting.meetingDetails.agenda.length > 0
          ? meeting.meetingDetails.agenda
          : [];
  const agendaCandidateById = new Map((Array.isArray(agendaEntries) ? agendaEntries : []).map((item, index) => {
    const id = getAgendaItemId(item) || `${meetingId || 'meeting'}:${index}:${String(item?.agendaEvent?._id || item?.agendaEvent?.name || 'agenda').trim()}`;
    const speakerName = `${item?.assignedUser?.firstName || ''} ${item?.assignedUser?.lastName || ''}`.trim() || 'TBA';
    const speechTitle = String(item?.item_title || '').split('\n').map((line) => line.trim()).find(Boolean)
      || String(item?.agendaEvent?.name || '').trim()
      || `Agenda item ${index + 1}`;
    return [id, {
      id,
      speakerName,
      speechTitle,
      avatarSrc: resolveAvatarSrc(item?.assignedUser?.avatarUrl || item?.assignedUser?.avatar || ''),
      initials: getUserInitials(item?.assignedUser, speakerName)
    }];
  }));

  async function applyRandomizerOrder() {
    if (isSavingRandomizerOrder || randomizerOrder.agendaItemIds.length < 2) return;

    const orderedAgendaItemIds = agendaEntries.map(getAgendaItemId);
    if (orderedAgendaItemIds.some((agendaItemId) => !agendaItemId)) {
      toast.error('Unable to update the agenda because one or more items are missing an ID.', { position: 'bottom-left' });
      return;
    }

    const randomizedIds = randomizerOrder.agendaItemIds;
    const randomizedIdSet = new Set(randomizedIds);
    const presenterIndexes = orderedAgendaItemIds
      .map((agendaItemId, index) => randomizedIdSet.has(agendaItemId) ? index : -1)
      .filter((index) => index >= 0);

    if (presenterIndexes.length !== randomizedIds.length) {
      toast.error('Unable to match the randomized presenters to the agenda.', { position: 'bottom-left' });
      return;
    }

    const targetAgendaItemIds = [...orderedAgendaItemIds];
    presenterIndexes.forEach((agendaIndex, presenterIndex) => {
      targetAgendaItemIds[agendaIndex] = randomizedIds[presenterIndex];
    });

    setIsSavingRandomizerOrder(true);
    try {
      const workingAgendaItemIds = [...orderedAgendaItemIds];
      for (let targetIndex = 0; targetIndex < targetAgendaItemIds.length; targetIndex += 1) {
        const targetAgendaItemId = targetAgendaItemIds[targetIndex];
        const currentIndex = workingAgendaItemIds.indexOf(targetAgendaItemId);
        if (currentIndex === targetIndex) continue;

        const response = await fetch(`${process.env.REACT_APP_URL}/agendaItem/${targetAgendaItemId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authUser.token}`
          },
          body: JSON.stringify({ position: targetIndex + 1 })
        });
        if (!response.ok) throw new Error('Unable to update the agenda order.');

        workingAgendaItemIds.splice(currentIndex, 1);
        workingAgendaItemIds.splice(targetIndex, 0, targetAgendaItemId);
      }

      const updatedContestJudgeEntries = contestJudgeEntries.map((entry) => (
        String(entry?.contestType || '') === randomizerOrder.contestType
          ? { ...entry, agendaItemIds: randomizedIds }
          : entry
      ));
      const setupResponse = await fetch(`${process.env.REACT_APP_URL}/contesthub/meetings/${meetingId}/setup`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authUser.token}`
        },
        body: JSON.stringify({
          contestTypes: assignedContestTypeIds,
          contestJudges: updatedContestJudgeEntries
        })
      });
      if (!setupResponse.ok) throw new Error('Unable to update the contest agenda items to judge.');

      setContestJudgeEntries(updatedContestJudgeEntries);
      await Promise.resolve(getMeeting?.());
      toast.success('Randomized presenter order applied to the agenda and contest setup.', { position: 'bottom-left' });
    } catch (error) {
      toast.error(error.message || 'Unable to update the agenda order.', { position: 'bottom-left' });
    } finally {
      setIsSavingRandomizerOrder(false);
    }
  }

  const assignmentRolesCount = Array.from(new Set(
    (Array.isArray(agenda) ? agenda : [])
      .map((item) => String(item?.meetingRole?._id || item?.meetingRole?.id || item?.meetingRole?.name || '').trim())
      .filter(Boolean)
  )).length;
  const timerEntriesCount = (Array.isArray(agenda) ? agenda : [])
    .filter((item) => Boolean(item?.timer))
    .length;
  const attendanceCount = Array.isArray(meeting?.meetingDetails?.attendance)
    ? meeting.meetingDetails.attendance.length
    : 0;

  switch (activeTool) {
    case 'assignments':
      modalTitle = 'Role Assignments';
      modalFooterMeta = (
        <span className='assignments-modal-meta'>
          {assignmentRolesCount} role{assignmentRolesCount === 1 ? '' : 's'} in agenda
        </span>
      );
      ModalBodyComponent = <AssignmentsCard getMeeting={getMeeting} getRoleAssignment={getRoleAssignment} assignments={assignments} agenda={agenda} meeting={meeting} />;
      break;
    case 'attendance':
      modalTitle = 'Attendance';
      modalFooterMeta = (
        <span className='assignments-modal-meta'>
          {attendanceCount} attendee{attendanceCount === 1 ? '' : 's'}
        </span>
      );
      ModalBodyComponent = <AttendanceCard meeting={meeting} getMeeting={getMeeting} />;
      break;
    case 'polls':
      modalTitle = 'Polls';
      modalFooterMeta = (
        <span className='assignments-modal-meta'>
          {toolCounts.polls} poll{toolCounts.polls === 1 ? '' : 's'}
        </span>
      );
      ModalBodyComponent = <PollCard agenda={agenda} meeting={meeting} onCountChange={(count) => updateToolCount('polls', count)} />;
      break;
    case 'timer':
      modalTitle = 'Time Report';
      modalFooterMeta = (
        <span className='timer-report-meta'>
          {timerEntriesCount} entr{timerEntriesCount === 1 ? 'y' : 'ies'}
        </span>
      );
      ModalBodyComponent = <TimerCard agenda={agenda} meeting={meeting} getMeeting={getMeeting} />;
      break;
    case 'remoteDisplays':
      modalTitle = 'Remote Displays';
      ModalBodyComponent = <TimerCard agenda={agenda} meeting={meeting} getMeeting={getMeeting} remoteDisplaysOnly />;
      break;
    case 'registerDisplay':
      modalTitle = 'Register This Screen';
      ModalBodyComponent = <RemoteDisplayRegistration {...remoteDisplayRegistration} />;
      break;
    case 'events':
      modalTitle = 'Events';
      modalFooterMeta = (
        <span className='assignments-modal-meta'>
          {toolCounts.events} event{toolCounts.events === 1 ? '' : 's'}
        </span>
      );
      ModalBodyComponent = <EventsCard onCountChange={(count) => updateToolCount('events', count)} />;
      break;
    case 'roles':
      modalTitle = 'Roles';
      modalFooterMeta = (
        <span className='assignments-modal-meta'>
          {toolCounts.roles} role{toolCounts.roles === 1 ? '' : 's'}
        </span>
      );
      ModalBodyComponent = <RolesCard onCountChange={(count) => updateToolCount('roles', count)} />;
      break;
    case 'evaluationTopics':
      modalTitle = 'Evaluation Topics';
      ModalBodyComponent = <EvaluationTopicsCard />;
      break;
    case 'contestHub':
      break;
    case 'contestRandomizer':
      modalTitle = 'Contest Randomizer';
      const selectedContestJudgeEntry = contestJudgeEntries.find((entry) => String(entry?.contestType || '') === selectedRandomizerContest);
      const hasRandomizedSelectedContest = randomizedContestTypes.includes(selectedRandomizerContest);
      const selectedAgendaItemIds = (selectedContestJudgeEntry?.agendaItemIds || []).map(String);
      const displayedAgendaItemIds = randomizerOrder.contestType === selectedRandomizerContest
        && randomizerOrder.agendaItemIds.length === selectedAgendaItemIds.length
        ? randomizerOrder.agendaItemIds
        : selectedAgendaItemIds;
      const selectedContestPresenters = displayedAgendaItemIds
        .map((agendaItemId) => agendaCandidateById.get(String(agendaItemId)))
        .filter(Boolean);
      ModalBodyComponent = meetingContests.length > 0
        ? <>
            <div className="d-flex flex-wrap gap-2 mb-3">
              {meetingContests.map((contest) => {
                const contestId = String(contest._id || contest.id || '');
                return <Button key={contestId} variant="primary" active={contestId === selectedRandomizerContest} onClick={() => selectRandomizerContest(contestId)}>
                  {contest.name}
                </Button>;
              })}
            </div>
            {selectedContestPresenters.length > 0
              ? <div>
                  <div className="d-flex justify-content-end mb-3">
                    <Button variant={hasRandomizedSelectedContest ? 'danger' : 'success'} onClick={() => randomizePresenters(selectedAgendaItemIds)} disabled={isRandomizing || selectedAgendaItemIds.length < 2}>
                      <i className="bi bi-shuffle me-2" aria-hidden="true" />
                      {isRandomizing ? 'Randomizing...' : 'Randomize'}
                    </Button>
                  </div>
                  {selectedContestPresenters.map((presenter) => (
                    <Card className="mb-2 contest-randomizer-presenter-card" key={`${presenter.id}-${randomizerRound}`}>
                      <Card.Body className="d-flex align-items-center gap-3">
                        <div className="contest-judge-avatar">
                          {presenter.avatarSrc
                            ? <img src={presenter.avatarSrc} alt={`Avatar for ${presenter.speakerName}`} className="contest-judge-avatar-image" />
                            : presenter.initials
                              ? <span className="contest-judge-avatar-fallback">{presenter.initials}</span>
                              : <i className="bi bi-person-fill contest-judge-avatar-fallback" aria-hidden="true" />}
                        </div>
                        <div>
                          <div className="contest-judge-speaker-name">{presenter.speakerName}</div>
                          <div className="contest-judge-speech-title">{presenter.speechTitle}</div>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                  <div className="d-flex justify-content-end mt-3">
                    <Button
                      variant="primary"
                      onClick={applyRandomizerOrder}
                      disabled={isRandomizing || isSavingRandomizerOrder || randomizerOrder.contestType !== selectedRandomizerContest}
                    >
                      {isSavingRandomizerOrder ? 'Applying Order...' : 'Apply Randomized Order'}
                    </Button>
                  </div>
                </div>
              : <p className="text-muted mb-0">No agenda items are assigned to judge for this contest.</p>}
          </>
        : <p className="text-muted mb-0">No contests are assigned to this meeting.</p>;
      break;
    default:
      if (activeContest) {
        modalTitle = activeContest.name;
        const visibleSections = (Array.isArray(activeContest.audienceSections) ? activeContest.audienceSections : [])
          .filter((section) => section?.visible !== false && String(section?.title || '').trim())
          .sort((left, right) => Number(left?.displayOrder || 0) - Number(right?.displayOrder || 0));
        ModalBodyComponent = visibleSections.length > 0
          ? <div className="contest-information-sections">{visibleSections.map((section, index) => <Card className="contest-information-section" key={section._id || `${section.title}-${index}`}><Card.Body><Card.Title as="h3">{section.title}</Card.Title><Card.Text as="div">{section.content}</Card.Text></Card.Body></Card>)}</div>
          : <p className="text-muted">No contest information is currently available.</p>;
      }
      break;
  }

  if (activeTool) {
    modalHeaderActions = (
      <Button
        type="button"
        variant="link"
        className="standard-modal-collapse-btn"
        onClick={handleReloadModalContent}
        disabled={reloading}
        aria-label={`Reload ${modalTitle || 'modal'} content`}
        title={`Reload ${modalTitle || 'modal'} content`}
      >
        <i className={`bi bi-arrow-clockwise${reloading ? ' meeting-modal-reload-icon--spinning' : ''}`} aria-hidden="true" />
      </Button>
    );
  }

  return (
    <>
      {/* Toggle button */}
      <div ref={toggleRef} className="right-menu">
        <button
          className="right-menu-toggle"
          onClick={() => setMenuOpen(v => !v)}
          aria-label="Toggle meeting tools"
        >
          <i className="bi bi-sliders2"></i>
        </button>
      </div>

      {/* Slide-in panel */}
      <RightSidebar label="Meeting Tools" open={menuOpen} onClose={() => setMenuOpen(false)} panelRef={panelRef} keepOpenOnAction>
            <div className="right-menu-header">
              <span className="right-menu-title">Meeting Tools</span>
              <button className="right-menu-close" onClick={() => setMenuOpen(false)} aria-label="Close menu">
                &times;
              </button>
            </div>
            <div className="right-menu-items">
              {visibleTools.map(tool => (
                <button
                  key={tool.key}
                  className="right-menu-item"
                  onClick={() => handleToolClick(tool.key)}
                >
                  <i className={tool.icon}></i>
                  <span>{tool.label}</span>
                </button>
              ))}
              {isContestMeeting && isTimerManager ? (
                <>
                  <hr className="right-menu-divider" />
                  <button
                    className="right-menu-item"
                    onClick={() => handleToolClick('contestRandomizer')}
                  >
                    <i className="bi bi-shuffle"></i>
                    <span>Contest Randomizer</span>
                  </button>
                </>
              ) : null}
              {isWebmaster && (
                <>
                  <hr className="right-menu-divider" />
                  <button
                    className="right-menu-item"
                    onClick={handleToggleMeetingLock}
                    disabled={isUpdatingLock}
                  >
                    <i className={`bi ${isTemporarilyUnlocked ? 'bi-lock-fill' : 'bi-unlock-fill'}`}></i>
                    <span>
                      {isTemporarilyUnlocked
                        ? `Lock Meeting Now (unlocked until ${unlockedUntil.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})`
                        : 'Unlock Meeting (2h)'}
                    </span>
                  </button>
                </>
              )}
            </div>
      </RightSidebar>

      <ContestHubSetupModal
        navigationParent="configuration"
        meeting={meeting}
        agenda={agenda}
        assignments={assignments}
        show={showModal && activeTool === 'contestHub'}
        onClose={handleCloseModal}
        isLocked={isLocked}
      />
      {activeTool !== 'contestHub' && <StandardModal
        navigationParent="configuration"
        show={showModal}
        handleClose={handleCloseModal}
        title={modalTitle}
        footerMeta={modalFooterMeta}
        headerActions={modalHeaderActions}
        style={{ maxWidth: '95%', marginTop: '4vh' }}
        ModalBodyComponent={(
          <Container>
            <fieldset disabled={isLocked} style={{ border: 'none', padding: 0, margin: 0 }}>
              <div key={`${activeTool || 'none'}-${reloadTick}`}>
                {ModalBodyComponent}
              </div>
            </fieldset>
          </Container>
        )}
        ModalFooterComponent={<Button variant="secondary" onClick={handleCloseModal}>Close</Button>}
      />}
    </>
  );
}
